import type { JobSeekerProfileLocationTypeInput } from '../../../../../../../../__generated__/types.gqlgenerated';

import { useUpdateJobSeekerProfileLocationMutation } from './__generated__/useUpdateJobSeekerProfileLocation.gqlgenerated';
import { useUpdateJobSeekerProfileResumeLocationMutation } from './__generated__/useUpdateJobSeekerProfileResumeLocation.gqlgenerated';

import { useCallback } from 'react';

/**
 * Unified location-save hook that routes to the correct mutation depending on
 * whether the jobseeker has a resume.
 *
 * If a resumeId is provided, the resume's location is updated via
 * `updateJobSeekerProfileResume`. Once a resume exists, profile defaultInfo
 * cannot be written to (PGAPI_E-011), so we must use the resume path.
 *
 * Otherwise, the profile's defaultInfo contact location is updated via
 * `updateJobSeekerProfile`.
 */
export function useUpdateJobSeekerLocation() {
    const [updateProfileLocationMutation] = useUpdateJobSeekerProfileLocationMutation();
    const [updateResumeLocationMutation] = useUpdateJobSeekerProfileResumeLocationMutation();

    return useCallback(
        async (
            location: JobSeekerProfileLocationTypeInput,
            resumeId: string | undefined,
        ) => {
            if (resumeId) {
                const { data } = await updateResumeLocationMutation({
                    variables: {
                        input: {
                            resume: { id: resumeId, location },
                        },
                    },
                });
                const resume = data?.updateJobSeekerProfileResume?.resume;
                if (!resume?.id) {
                    throw new Error(
                        'UpdateJobSeekerProfileResumeLocation returned no resume',
                    );
                }
                return resume;
            }

            const { data } = await updateProfileLocationMutation({
                variables: {
                    input: {
                        profile: {
                            defaultInfo: {
                                contactInformation: { location },
                            },
                        },
                    },
                },
            });
            const profile = data?.updateJobSeekerProfile?.profile;
            if (!profile?.accountKey) {
                throw new Error('UpdateJobSeekerProfileLocation returned no profile');
            }
            return profile;
        },
        [updateProfileLocationMutation, updateResumeLocationMutation],
    );
}
